import type {
  MetaAd,
  MetaAdset,
  MetaCampaign,
  MetaPerf,
  MetaStructureResponse,
} from '@nono/contract';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useFilters } from '@/lib/filters';

/*
 * Data + derived facts for the Meta section. One query (the live account
 * structure), and pure functions that turn it into what the pages draw:
 * result type per ad set, cost per result in NOK, a verdict per ad, and the
 * health checks that hold the account up against the blueprint.
 */

export type Period = 'lifetime' | 'recent';
export type ResultKind = 'booking' | 'lead';
export type Verdict = 'good' | 'ok' | 'bad' | 'none' | 'young';

/** The live account; «recent» = the period chosen in the date picker. */
export function useMetaStructure() {
  const { from, to } = useFilters();
  return useQuery({
    queryKey: ['meta-structure', from, to],
    queryFn: async (): Promise<{ data: MetaStructureResponse; snapshot: boolean }> => {
      try {
        return { data: await api.metaStructure({ from, to }), snapshot: false };
      } catch (err) {
        // DEV ONLY: the local backend has no database, so design against a real
        // snapshot of the account (its period is fixed). Dropped in prod.
        if (import.meta.env.DEV) {
          const mod = await import('./structure.dev.json');
          return { data: mod.default as MetaStructureResponse, snapshot: true };
        }
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev, // keep the board while a new period loads
  });
}

/** "06.10–19.10" */
export function periodLabel(d: Pick<MetaStructureResponse, 'recentFrom' | 'recentTo'>): string {
  const f = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
  return `${f(d.recentFrom)}–${f(d.recentTo)}`;
}

/* ── Creative metrics (the numbers that say if an ad stops the thumb) ────── */

export interface CreativeStats {
  isVideo: boolean;
  hook: number | null; // 3-s plays / impressions
  hold: number | null; // thruplays / 3-s plays
  avgWatch: number | null; // seconds
  /** Share of impressions that reached each point of the video. */
  curve: { label: string; v: number | null }[];
  ctr: number | null; // link clicks / impressions
  outboundCtr: number | null;
  engagementRate: number | null;
  cpm: number | null; // NOK
  cpc: number | null; // NOK
}

const rate = (n: number, d: number) => (d > 0 ? n / d : null);

export function creativeStats(p: MetaPerf, fx: number): CreativeStats {
  const isVideo = p.video3s > 0;
  return {
    isVideo,
    hook: isVideo ? rate(p.video3s, p.impressions) : null,
    hold: isVideo ? rate(p.thruplays, p.video3s) : null,
    avgWatch: p.watchImpr > 0 ? p.watchSecImpr / p.watchImpr : null,
    curve: [
      { label: '3 s', v: rate(p.video3s, p.impressions) },
      { label: '25 %', v: rate(p.p25, p.impressions) },
      { label: '50 %', v: rate(p.p50, p.impressions) },
      { label: '75 %', v: rate(p.p75, p.impressions) },
      { label: '100 %', v: rate(p.p100, p.impressions) },
    ],
    ctr: rate(p.clicks, p.impressions),
    outboundCtr: rate(p.outboundClicks, p.impressions),
    engagementRate: rate(p.engagement, p.impressions),
    cpm: p.impressions > 0 ? ((p.spend * fx) / p.impressions) * 1000 : null,
    cpc: p.clicks > 0 ? (p.spend * fx) / p.clicks : null,
  };
}

/*
 * Rules of thumb for Meta video (not Nono's own history yet): a hook rate of
 * 30 %+ stops the scroll well, under 20 % is weak; a hold rate of 40 %+ keeps
 * people, under 20 % loses them. Shown as a hint, never as a verdict.
 */
export function hookTone(v: number | null): 'good' | 'bad' | null {
  if (v === null) return null;
  return v >= 0.3 ? 'good' : v < 0.2 ? 'bad' : null;
}
export function holdTone(v: number | null): 'good' | 'bad' | null {
  if (v === null) return null;
  return v >= 0.4 ? 'good' : v < 0.2 ? 'bad' : null;
}
export const pctStr = (v: number | null, dp = 1) =>
  v === null ? '–' : `${(v * 100).toFixed(dp).replace('.', ',')} %`;

export const resultKind = (s: MetaAdset): ResultKind =>
  s.conversionEvent === 'SCHEDULE' ? 'booking' : 'lead';

export const resultsOf = (p: MetaPerf, kind: ResultKind) =>
  kind === 'booking' ? p.schedules : p.leads;

/** Cost per result in NOK, or null when there were no results. */
export function costPer(p: MetaPerf, kind: ResultKind, fx: number): number | null {
  const n = resultsOf(p, kind);
  return n > 0 ? (p.spend * fx) / n : null;
}

/*
 * Verdict bands in NOK, from the account's own history (META, 19.10):
 * a website booking cost ~475 kr on average, a form lead ~140 kr. An ad is
 * "good" well under that, "bad" well over, and "none" when it spent real money
 * for nothing. Ads under MIN_SPEND have not had a fair chance yet.
 */
const BANDS: Record<ResultKind, { good: number; bad: number }> = {
  booking: { good: 400, bad: 700 },
  lead: { good: 110, bad: 200 },
};
const MIN_SPEND_NOK = 150;

export function verdict(p: MetaPerf, kind: ResultKind, fx: number): Verdict {
  const spend = p.spend * fx;
  const c = costPer(p, kind, fx);
  if (c === null) return spend >= MIN_SPEND_NOK * 2 ? 'none' : 'young';
  if (spend < MIN_SPEND_NOK) return 'young';
  if (c <= BANDS[kind].good) return 'good';
  if (c >= BANDS[kind].bad) return 'bad';
  return 'ok';
}

export const isLive = (s: { status: string }) => s.status === 'active';

export interface Flat {
  campaign: MetaCampaign;
  adset: MetaAdset;
  ad: MetaAd;
}
export function flatAds(d: MetaStructureResponse): Flat[] {
  return d.campaigns.flatMap((campaign) =>
    campaign.adsets.flatMap((adset) => adset.ads.map((ad) => ({ campaign, adset, ad }))),
  );
}

/**
 * What a result costs, counting only the money that was spent on that kind of
 * result: booking ad sets for bookings, instant-form ad sets for leads. Dividing
 * ALL spend by bookings (as a blended number would) punishes bookings for money
 * that was buying forms.
 */
export function unitCosts(d: MetaStructureResponse, period: Period) {
  const fx = d.fxAedToNok;
  let bSpend = 0;
  let bookings = 0;
  let lSpend = 0;
  let leads = 0;
  for (const c of d.campaigns)
    for (const s of c.adsets) {
      const p = s[period];
      if (resultKind(s) === 'booking') {
        bSpend += p.spend;
        bookings += p.schedules;
      } else if (s.optimizationGoal === 'QUALITY_LEAD' || s.optimizationGoal === 'LEAD_GENERATION') {
        lSpend += p.spend;
        leads += p.leads;
      }
    }
  return {
    costPerBooking: bookings ? (bSpend * fx) / bookings : null,
    costPerLead: leads ? (lSpend * fx) / leads : null,
  };
}

/* ── Health: the account against the blueprint ───────────────────────────── */

export interface Finding {
  id: string;
  tone: 'bad' | 'warn' | 'info';
  title: string;
  body: string;
  /** Entity ids the finding is about, so the map can light them up. */
  ids: string[];
}

export function healthChecks(d: MetaStructureResponse): Finding[] {
  const out: Finding[] = [];
  const fx = d.fxAedToNok;
  const liveCampaigns = d.campaigns.filter(isLive);
  const liveAdsets = liveCampaigns.flatMap((c) => c.adsets.filter(isLive));

  // 1. Is anything optimising for bookings?
  const bookingLive = liveAdsets.filter((s) => resultKind(s) === 'booking');
  if (!bookingLive.length) {
    const last = d.campaigns
      .flatMap((c) => c.adsets)
      .filter((s) => resultKind(s) === 'booking')
      .map((s) => s.name);
    out.push({
      id: 'no-booking',
      tone: 'bad',
      title: 'Ingen aktiv kampanje optimaliserer for bookinger',
      body: `Alt som går nå optimaliserer for skjemalead. Meta finner det du ber om: billige skjemaer, ikke folk som booker. ${last.length ? `Sist brukt: ${last.length} Schedule-annonsesett, alle stoppet.` : ''}`,
      ids: liveCampaigns.map((c) => c.id),
    });
  }

  // 2. CBO starving ad sets.
  for (const c of liveCampaigns) {
    const sets = c.adsets.filter(isLive);
    if (c.budgetLevel !== 'campaign' || sets.length < 2) continue;
    const total = sets.reduce((n, s) => n + s.recent.spend, 0);
    if (total <= 0) continue;
    const starved = sets.filter((s) => s.recent.spend / total < 0.1);
    if (starved.length) {
      out.push({
        id: `cbo-${c.id}`,
        tone: 'warn',
        title: `Budsjettet sulter ${starved.length} av ${sets.length} annonsesett`,
        body: `I «${c.name}» fikk ${starved.map((s) => `«${short(s.name)}» ${pct(s.recent.spend / total)}`).join(', ')} av pengene i perioden ${periodLabel(d)}. CBO gir alt til den gamle vinneren, så det nye blir aldri testet.`,
        ids: starved.map((s) => s.id),
      });
    }
  }

  // 3. Too many ads per ad set for our volume.
  for (const s of liveAdsets) {
    const live = s.ads.filter(isLive);
    if (live.length <= 6) continue;
    const silent = live.filter((a) => a.recent.spend * fx < 20);
    out.push({
      id: `ads-${s.id}`,
      tone: 'warn',
      title: `${live.length} aktive annonser i ett annonsesett`,
      body: `«${short(s.name)}»: ${silent.length} av dem fikk under 20 kr i perioden ${periodLabel(d)}. Meta gir pengene til 1–3 annonser. Behold 4–6.`,
      ids: silent.map((a) => a.id),
    });
  }

  // 4. Learning volume.
  const weekly = liveAdsets.map((s) => ({
    s,
    perWeek: (resultsOf(s.recent, resultKind(s)) / d.recentDays) * 7,
  }));
  if (weekly.length > 1) {
    out.push({
      id: 'learning',
      tone: 'info',
      title: `${weekly.length} aktive annonsesett deler et lite volum`,
      body: `Meta vil ha ~50 resultater i uka per annonsesett for å lære. Nå: ${weekly.map((w) => `«${short(w.s.name)}» ${dec(w.perWeek)}`).join(', ')} per uke. Færre annonsesett lærer fortere.`,
      ids: liveAdsets.map((s) => s.id),
    });
  }

  // 5. Saturation.
  const hot = liveAdsets.filter((s) => (s.lifetime.frequency ?? 0) >= 5);
  if (hot.length) {
    out.push({
      id: 'freq',
      tone: 'warn',
      title: 'De samme menneskene ser annonsene igjen og igjen',
      body: `Frekvens ${hot.map((s) => `${dec(s.lifetime.frequency ?? 0)} i «${short(s.name)}»`).join(', ')}. Målgruppen innenfor radiusen i Moss er liten. Utvid radiusen eller bytt kreativ oftere.`,
      ids: hot.map((s) => s.id),
    });
  }

  // 6. Objective that doesn't match the goal (history, still worth knowing).
  for (const c of d.campaigns) {
    if (c.objective === 'OUTCOME_ENGAGEMENT' && c.adsets.some((s) => resultKind(s) === 'booking')) {
      out.push({
        id: `obj-${c.id}`,
        tone: 'info',
        title: 'Booking-kampanjen kjørte med målet «Engasjement»',
        body: `«${c.name}» optimaliserte for Schedule, men kampanjemålet var Engasjement. Neste booking-kampanje bør ha målet Leads (nettside) eller Salg.`,
        ids: [c.id],
      });
    }
  }

  return out;
}

const dec = (x: number) => x.toFixed(1).replace('.', ',');
const pct = (x: number) => `${Math.round(x * 100)} %`;
/**
 * Ad set names are long and full of bookkeeping ("Moss || 20 May • Elias • VI HAR
 * ÅPNET • 7d"). Keep the part a person recognises: drop the city, the buyer,
 * date, attribution window and generic words, then take the last part left.
 */
const NOISE = /^(moss|elias|\d+d|lead|leads|video|conversion|website|s|sc|schedule|cbo:?|- lead)$/i;
const DATE = /^\d{1,2}\s+(jan|feb|mar|apr|mai|may|jun|jul|aug|sep|okt|oct|nov|des|dec)[a-z]*$/i;
export function short(name: string): string {
  const parts = name
    .split(/[•|]+/)
    .map((p) => p.replace(/\s+-\s+lead$/i, '').trim())
    .filter(Boolean);
  const keep = parts.filter((p) => !NOISE.test(p) && !DATE.test(p));
  if (keep.length) return keep[keep.length - 1]!;
  const date = parts.find((p) => DATE.test(p));
  const site = /website|conversion/i.test(name);
  return [date, site ? 'nettside' : null].filter(Boolean).join(' · ') || name;
}

/* ── Formatting ──────────────────────────────────────────────────────────── */

const NOK = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });
export const kr = (n: number | null | undefined) => (n == null ? '–' : `${NOK.format(n)} kr`);
export const int = (n: number) => NOK.format(n);
